import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Info } from "lucide-react";
import { Section, SectionTitle, TeamLogo, Reveal } from "../components/UIBits";
import { TOURNAMENT, CATEGORY_YEARS } from "../lib/constants";
import { useT } from "../lib/i18n";
import api from "../lib/api";

export default function StandingsPage() {
    const { t } = useT();
    const [cat, setCat] = useState(TOURNAMENT.categories[0]);
    const [data, setData] = useState(null);

    useEffect(() => {
        let active = true;
        api.get(`/standings/${cat}`).then(r => { if (active) setData(r.data); });
        const i = setInterval(() => api.get(`/standings/${cat}`).then(r => active && setData(r.data)), 15000);
        return () => { active = false; clearInterval(i); };
    }, [cat]);

    const TIEBREAKERS = [
        t("rules.r9_body").split(";")[0].replace(/^\d\)\s*/, "").trim(),
    ];
    // simpler: use the rule article body
    const tiebreakers = t("rules.r9_body").split(";").map(s => s.trim().replace(/^\d\)\s*/, "")).filter(Boolean);

    return (
        <div data-testid="standings-page">
            <Helmet><title>{`${t("standings.title_pre")} ${t("standings.title_accent")} — ${TOURNAMENT.name}`}</title></Helmet>
            <Section>
                <SectionTitle kicker={t("standings.kicker")} title={t("standings.title_pre")} accent={t("standings.title_accent")} />
                <div className="flex flex-wrap gap-2 mb-2">
                    {TOURNAMENT.categories.map((c) => (
                        <button
                            key={c}
                            data-testid={`tab-cat-${c}`}
                            onClick={() => setCat(c)}
                            className={`px-5 py-2 font-display text-lg tracking-widest uppercase angle-tag transition-all ${cat === c ? "bg-[#7FD93C] text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
                <div className="text-sm text-[#4FE0D5] mb-6 font-mono">{t("teams.born_in")} {CATEGORY_YEARS[cat]}</div>

                {!data ? (
                    <div className="text-white/50">{t("common.loading")}</div>
                ) : (
                    <>
                        <div className="overflow-x-auto hidden md:block">
                            <table className="tx-table" data-testid="standings-table">
                                <thead>
                                    <tr>
                                        <th>{t("standings.col_rank")}</th><th>{t("standings.col_team")}</th><th>{t("standings.col_played")}</th><th>{t("standings.col_wins")}</th><th>{t("standings.col_losses")}</th><th>{t("standings.col_ties")}</th>
                                        <th>{t("standings.col_gf")}</th><th>{t("standings.col_ga")}</th><th>{t("standings.col_diff")}</th><th>{t("standings.col_pim")}</th><th>{t("standings.col_pts")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.rows.map((r) => (
                                        <tr key={r.team_id} data-testid={`standings-row-${r.team_id}`}>
                                            <td className="font-display text-2xl text-[#7FD93C]">{r.rank}</td>
                                            <td><div className="flex items-center gap-3"><TeamLogo team={{ name: r.team_name, color: r.color, logo_base64: r.logo_base64 }} size={28} /><span className="font-semibold">{r.team_name}</span></div></td>
                                            <td>{r.PJ}</td>
                                            <td className="text-[#7FD93C] font-bold">{r.V}</td>
                                            <td>{r.D}</td>
                                            <td>{r.N}</td>
                                            <td>{r.BP}</td>
                                            <td>{r.BC}</td>
                                            <td className={r.DIFF >= 0 ? "text-[#7FD93C]" : "text-rose-400"}>{r.DIFF >= 0 ? "+" : ""}{r.DIFF}</td>
                                            <td>{r.PIM}</td>
                                            <td className="font-display text-xl text-[#4FE0D5]">{r.PTS}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile cards */}
                        <div className="md:hidden grid gap-3" data-testid="standings-cards">
                            {data.rows.map((r) => (
                                <div key={r.team_id} className="glass-card p-4" data-testid={`standings-card-${r.team_id}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="font-display text-3xl text-[#7FD93C] w-8 text-center shrink-0">{r.rank}</div>
                                        <TeamLogo team={{ name: r.team_name, color: r.color, logo_base64: r.logo_base64 }} size={32} />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold truncate">{r.team_name}</div>
                                            <div className="text-[10px] text-white/40 uppercase tracking-widest">{r.PJ} {t("standings.col_played")}</div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="font-display text-2xl text-[#4FE0D5] leading-none">{r.PTS}</div>
                                            <div className="text-[10px] text-white/40 uppercase tracking-widest">{t("standings.col_pts")}</div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-6 gap-1 text-center text-xs">
                                        <div><div className="text-[#7FD93C] font-bold text-sm">{r.V}</div><div className="text-white/40 text-[9px] uppercase">{t("standings.col_wins")}</div></div>
                                        <div><div className="text-sm">{r.D}</div><div className="text-white/40 text-[9px] uppercase">{t("standings.col_losses")}</div></div>
                                        <div><div className="text-sm">{r.N}</div><div className="text-white/40 text-[9px] uppercase">{t("standings.col_ties")}</div></div>
                                        <div><div className="text-sm">{r.BP}</div><div className="text-white/40 text-[9px] uppercase">{t("standings.col_gf")}</div></div>
                                        <div><div className="text-sm">{r.BC}</div><div className="text-white/40 text-[9px] uppercase">{t("standings.col_ga")}</div></div>
                                        <div><div className={`text-sm ${r.DIFF >= 0 ? "text-[#7FD93C]" : "text-rose-400"}`}>{r.DIFF >= 0 ? "+" : ""}{r.DIFF}</div><div className="text-white/40 text-[9px] uppercase">{t("standings.col_diff")}</div></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {data.rows.length === 0 && <div className="text-white/50 mt-6">{t("standings.no_data")}</div>}

                        <Reveal className="mt-12">
                            <div className="glass-card p-6">
                                <div className="flex items-start gap-3">
                                    <Info size={20} className="text-[#4FE0D5] mt-0.5" />
                                    <div className="flex-1">
                                        <div className="font-display text-xl tracking-widest text-[#4FE0D5] mb-3">{t("standings.legend_title")}</div>
                                        <ol className="text-sm text-white/70 space-y-1 list-decimal list-inside">
                                            {tiebreakers.map((tb, i) => <li key={i}>{tb}</li>)}
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    </>
                )}
            </Section>
        </div>
    );
}
